import { useQuery } from '@tanstack/react-query';

import { getReviewQueueFacets } from '@/api/prescriptions.api';
import { useActivePharmacy } from '@/auth/active-pharmacy';
import { useAuth } from '@/auth/auth-context';
import { usePharmacyFeatures } from '@/auth/features';
import { queryKeys } from '@/lib/query-keys';

/**
 * How many prescriptions are waiting to be read, for the chrome: the navbar
 * button and the sidebar badge, as `useNewOrderCount` is for orders.
 *
 * The same request and the SAME CACHE KEY as the counts on the prescriptions
 * queue, so the badge and the tab under it cannot disagree - reading one on
 * the queue invalidates it for the sidebar too. A push refetches it at once
 * (see `usePushRefresh`); the poll is the floor for a device without them.
 *
 * Returns 0 once loaded with none waiting, and undefined while it has never
 * loaded, so the chrome can draw nothing rather than flash "0".
 */
export function useNewPrescriptionCount(): number | undefined {
  const { can } = useAuth();
  const { pharmacyId } = useActivePharmacy();
  const features = usePharmacyFeatures();

  /* The same two gates the Prescriptions nav row is behind. */
  const allowed = Boolean(pharmacyId) && can('prescription:review') && features.prescriptions;

  const { data } = useQuery({
    queryKey: queryKeys.prescriptions.queueFacets,
    queryFn: getReviewQueueFacets,
    enabled: allowed,
    refetchInterval: 20_000,
    refetchIntervalInBackground: true,
  });

  if (!allowed) return 0;
  /* Uploaded and not yet opened: the ones nobody has started on. */
  return data ? (data.byStatus.UPLOADED ?? 0) : undefined;
}
